import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, USER_GROUPS_MODULE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const titles = {
	view: 'viewUserGroup',
	edit: 'editUserGroup',
	add: 'addUserGroup'
};

const UserGroupForm = NiceModal.create(({ mode, id, modalName }) => {
	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();
	const modal = useModal();
	const handleCloseModal = () => modal.remove();

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().required(),
					description: yup.string().nullable().required(),
					warehouseID: yup.number().required(),
					userIDs: yup.array().required()
				})
				.required(),
		[]
	);

	const defaultValues = {
		name: null,
		description: null,
		warehouseID: null,
		userIDs: null
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	const viewMode = mode === 'view';

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const URL =
				mode === 'add' ? USER_GROUPS_MODULE.ADD_WMS_USER_GROUP : USER_GROUPS_MODULE.UPDATE_WMS_USER_GROUP;

			const requestData =
				mode === 'add'
					? data
					: {
							...data,
							userGroupId: id
						};

			const response = await axiosPrivate.post(URL, requestData);

			if (response.status === 200) {
				showSuccess('userGroupSaved.successMessage');
				dispatch(updateDynamicListData(DYNAMICLISTS.USER_GROUPS));
				handleCloseModal();
			}
		} catch (error) {
			showError('userGroupSaved.errorMessage');
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		setValue('userIDs', null, { shouldValidate: true, shouldDirty: true });
	}, [watch('warehouseID')]);

	const fillForm = useCallback(async () => {
		try {
			const response = await axiosPrivate.post(USER_GROUPS_MODULE.GET_WMS_USER_GROUP, {
				userGroupId: id
			});

			const { data, status } = response;
			if (status === 200) {
				reset({
					name: data.data[0].name ?? null,
					description: data.data[0].description ?? null,
					warehouseID: data.data[0].warehouseID ?? null,
					userIDs: data.data[0].wosUserIds.split(',') ?? null
				});
			} else {
				AppLogger.error(response);
				showError();
				closeModal();
			}
		} catch (error) {
			AppLogger.error(error);
		}
	}, [id]);

	useEffect(() => {
		if (id) fillForm();
	}, [id]);

	return (
		<Modal fullwidth name={modalName} maxWidth="small">
			<ModalTitle>
				<Typography>{titles[mode] ?? 'viewUserGroup'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="warehouse" required isLoading={isLoading}>
						<AutoComplete
							name="warehouseID"
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							value={watch('warehouseID')}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
							setValue={(value) => {
								setValue('warehouseID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							viewMode={viewMode}
						/>
					</FormItem>
					<FormItem label="groupName" required isLoading={isLoading}>
						<StringInput name="name" disabled={viewMode} />
					</FormItem>
					<FormItem label="description" required isLoading={isLoading}>
						<StringInput name="description" disabled={viewMode} />
					</FormItem>
					<FormItem label="users" required isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.PICKER_USERS_BY_WAREHOUSE}
							name="userIDs"
							value={watch('userIDs')}
							setValue={(value) =>
								setValue('userIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								})
							}
							viewMode={viewMode}
							columns={[{ accessor: 'Name', width: 200 }]}
							selectionLabelAccessor={['Name']}
							multipleSelection
							filterObject={{
								warehouseID: watch('warehouseID')
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				{!viewMode && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'edit' : 'add'}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.error(errors);
						})}
						iconPos="right"
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default UserGroupForm;
